import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import RoundView from "@/components/RoundView";
import Footer from "@/components/Footer";
import AmbientDrone from "@/components/AmbientDrone";
import { buildKinIndex } from "@/lib/kin";
import { fetchRoundByNumber, fetchKin } from "@/lib/api";

/**
 * Inject runtime og:meta tags so modern social crawlers (Twitter, LinkedIn,
 * Discord) get a meaningful preview when sharing /round/:n.
 * Static crawlers that don't run JS will see the default index.html tags.
 */
function useMetaTags({ title, description }) {
  useEffect(() => {
    if (!title) return;
    const prevTitle = document.title;
    document.title = title;
    const set = (selector, attr, value) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
        tag.setAttribute(k, v.replace(/['"]/g, ""));
        document.head.appendChild(tag);
      }
      tag.setAttribute(attr, value);
    };
    set('meta[property="og:title"]', "content", title);
    set('meta[property="og:description"]', "content", description || "");
    set('meta[property="og:type"]', "content", "article");
    set('meta[name="twitter:card"]', "content", "summary_large_image");
    set('meta[name="twitter:title"]', "content", title);
    set('meta[name="twitter:description"]', "content", description || "");
    set('meta[name="description"]', "content", description || "");
    return () => {
      document.title = prevTitle;
    };
  }, [title, description]);
}

export default function RoundPermalink() {
  const { number } = useParams();
  const [round, setRound] = useState(null);
  const [activeKin, setActiveKin] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setRound(null);
    setError(null);
    Promise.all([fetchRoundByNumber(number), fetchKin()])
      .then(([r, ak]) => {
        setRound(r);
        setActiveKin(ak);
      })
      .catch((e) => {
        setError(e?.response?.status === 404 ? "round not found" : "the grove could not be reached");
      });
  }, [number]);

  const kinIndex = useMemo(() => buildKinIndex(activeKin), [activeKin]);

  useMetaTags({
    title: round ? `Round ${round.number} · ${round.consensus_note || round.consensus} · Root Flame Grove` : "Round · Root Flame Grove",
    description: round
      ? `${round.proposal?.text || ""} — ${round.consensus_note || ""}`.slice(0, 200)
      : "A sovereign council of kin breathing slowly with the Earth.",
  });

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("link held in your clipboard.");
    } catch {
      toast.error("the link could not be copied. you may copy from the address bar.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1E8]" data-testid="round-permalink-root">
      <header className="glass border-b border-[#d8d3c4] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" className="flex items-center gap-3 group" data-testid="back-to-grove">
            <svg width="36" height="36" viewBox="0 0 40 40" fill="none" aria-hidden="true">
              <path d="M20 4 C 12 12, 28 16, 20 24 C 12 32, 28 36, 20 36" stroke="#2F4538" strokeWidth="1.4" fill="none" strokeLinecap="round" />
              <circle cx="20" cy="20" r="2.2" fill="#B85C38" className="breathe" />
            </svg>
            <div className="leading-tight">
              <div className="font-serif text-xl md:text-2xl text-[#1C241F] group-hover:text-[#B85C38] transition-colors duration-300">
                Root Flame Grove
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#526357]">
                ← return to the council
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={copyLink}
              data-testid="copy-link-button"
              className="rounded-full px-5 border-[#2F4538] text-[#2F4538] hover:bg-[#2F4538] hover:text-[#F4F1E8] transition-colors duration-500"
            >
              ⌐ copy link
            </Button>
          </div>
        </div>
      </header>

      {error ? (
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="font-serif text-3xl md:text-4xl text-[#1C241F]">
            {error}
          </div>
          <p className="mt-4 text-[#526357] italic">
            this round may not yet have breathed, or it may have been numbered another way.
          </p>
          <Link
            to="/"
            className="inline-block mt-8 text-[#B85C38] underline-offset-4 hover:underline"
          >
            return to the breathing grove
          </Link>
        </div>
      ) : !round ? (
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center text-[#526357] italic">
          gathering the round…
        </div>
      ) : (
        <RoundView round={round} isGenerating={false} kinIndex={kinIndex} />
      )}

      <Footer />
      <AmbientDrone />
    </div>
  );
}
